// INSIGHTS — the book as evidence. Every chart answers one question and links back to the people
// behind the number: Is my book healthy? Where are the protection gaps? When did my portfolio come
// in? Am I active? Where do applications stall? (Charts follow the AVRASTRO data-viz method: one hue for
// magnitude, status colours only with labels, hover on every mark, text alternative for each.)
import { useMemo, useState } from 'react';
import { portfolioInsights, bookAnalytics, bookHealth, type CoverageArea } from '@vero/intelligence';
import { addDays } from '@vero/domain';
import { useRuntime } from '../core/runtime.tsx';
import { Avatar, Empty, Stat } from '../core/ui.tsx';
import { navigate, personPath } from '../core/router.ts';
import { BAND_COLOR, Band, Dial } from '../core/viz.tsx';

export function InsightsView() {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const ins = useMemo(() => portfolioInsights(view, today), [view, today]);
  const an = useMemo(() => bookAnalytics(view, today), [view, today]);
  const book = useMemo(() => bookHealth(view, today), [view, today]);
  const [gapArea, setGapArea] = useState<CoverageArea | null>(null);
  const maxCarrier = Math.max(1, ...ins.topCarriers.map(([, v]) => v));
  const maxGap = Math.max(1, ...an.gaps.map((g) => g.personIds.length));
  const maxCohort = Math.max(1, ...an.cohorts.map((c) => c.annual));
  const maxAct = Math.max(1, ...an.activity.map((a) => a.count));
  const maxFunnel = Math.max(1, ...an.funnel.map((f) => f.count));
  const totalHealth = an.health.reduce((s, h) => s + h.count, 0);
  const selected = gapArea ? an.gaps.find((g) => g.area === gapArea) : an.gaps[0];
  const wd = new Intl.DateTimeFormat(rt.locale, { weekday: 'short' });
  const start = an.activity[0]?.day ?? today;
  const totalDone = an.activity.reduce((s, a) => s + a.count, 0);

  return (
    <div data-testid="insights">
      <div className="page-head"><div><h1>{t('insights.title')}</h1><p>{t('insights.subtitle')}</p></div></div>
      <div className="stats">
        <Stat value={i18n.money(ins.totalAnnual, undefined, { compact: true })} label={t('today.stat_portfolio')} />
        <Stat value={ins.clients} label={t('today.stat_clients')} />
        <Stat value={ins.activity.calls} label={`${t('insights.calls')} · 30d`} />
        <Stat value={ins.activity.meetings} label={`${t('insights.meetings')} · 30d`} />
        <Stat value={ins.activity.casesIssued} label={`${t('insights.cases_issued')} · 30d`} />
        <Stat value={ins.activity.newClients} label={`${t('insights.new_clients')} · 30d`} />
      </div>

      <div className="grid grid-3" style={{ marginBottom: 18 }}>
        <section className="card card-pad" data-testid="insights-health">
          <div className="section-title">{t('insights_v2.health_dist')}</div>
          {totalHealth ? <>
            <div className="row" style={{ gap: 16, marginBottom: 12 }}>
              <Dial score={book.score} band={book.band} size={88} stroke={8} label="/100" />
              <div className="stack" style={{ gap: 6, flex: 1 }}>
                {an.health.map((h) => (
                  <div key={h.band} className="row" style={{ gap: 8 }}>
                    <span style={{ width: 70 }}><Band band={h.band} label={t(`health.band.${h.band}`)} /></span>
                    <span className="grow bar" style={{ height: 8 }}><i style={{ width: `${(h.count / totalHealth) * 100}%`, background: BAND_COLOR[h.band] }} /></span>
                    <b className="num small" style={{ width: 28, textAlign: 'end' }}>{h.count}</b>
                  </div>
                ))}
              </div>
            </div>
            {an.atRisk.length > 0 && <>
              <div className="upper" style={{ margin: '6px 0' }}>{t('insights_v2.at_risk')}</div>
              {an.atRisk.slice(0, 5).map((r) => { const p = view.get('persons', r.personId); return (
                <div key={r.personId} className="agenda-item" style={{ gridTemplateColumns: 'auto minmax(0,1fr) auto', padding: '6px 8px' }} onClick={() => navigate(personPath(r.personId))} role="button" tabIndex={0}>
                  <Avatar name={p?.displayName ?? ''} size="xs" /><span className="person-name one-line small">{p?.displayName}</span><b className="num small" style={{ color: BAND_COLOR[r.band] }}>{r.score}</b>
                </div>); })}
            </>}
          </> : <Empty icon="insights">{t('health.book_empty')}</Empty>}
        </section>

        <section className="card card-pad" data-testid="insights-gaps">
          <div className="section-title">{t('coverage.opportunities')}</div>
          <p className="tiny muted" style={{ margin: '-4px 0 12px' }}>{t('coverage.opportunities_hint')}</p>
          {an.gaps.length ? <>
            <div className="bars">
              {an.gaps.map((g) => (
                <button key={g.area} className="brow" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'start', color: 'inherit', opacity: selected?.area === g.area ? 1 : 0.7 }} onClick={() => setGapArea(g.area)} aria-pressed={selected?.area === g.area} data-tip={`${t(`coverage.area.${g.area}`)} · ${g.personIds.length}`}>
                  <span className="one-line">{t(`coverage.area.${g.area}`)}</span>
                  <span className="track"><i style={{ width: `${(g.personIds.length / maxGap) * 100}%` }} /></span>
                  <b className="num">{g.personIds.length}</b>
                </button>
              ))}
            </div>
            {selected && <div className="row wrap" style={{ gap: 6, marginTop: 12 }}>
              {selected.personIds.slice(0, 10).map((id) => { const p = view.get('persons', id); return <button key={id} className="name-chip" onClick={() => navigate(personPath(id))}><Avatar name={p?.displayName ?? ''} size="xs" />{p?.displayName}</button>; })}
              {selected.personIds.length > 10 && <span className="chip">+{selected.personIds.length - 10}</span>}
            </div>}
          </> : <Empty icon="shield">{t('coverage.gaps', { count: 0 })}</Empty>}
        </section>

        <section className="card card-pad" data-testid="insights-funnel">
          <div className="section-title">{t('insights_v2.funnel')}</div>
          <div className="funnel">
            {an.funnel.map((f) => (
              <div key={f.stage} className="funnel-row" data-tip={`${i18n.enumLabel('caseStage', f.stage)} · ${f.count}`}>
                <span className="one-line small">{i18n.enumLabel('caseStage', f.stage)}</span>
                <span className="track"><i style={{ width: `${(f.count / maxFunnel) * 100}%` }} /></span>
                <b>{f.count}</b>
              </div>
            ))}
          </div>
          <p className="tiny muted" style={{ margin: '10px 0 0' }}>{t('insights_v2.funnel_hint')}</p>
        </section>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 1fr)', marginBottom: 18 }}>
        <section className="card card-pad" data-testid="insights-cohorts">
          <div className="section-title">{t('insights_v2.cohorts')}</div>
          {an.cohorts.length ? (
            <div role="img" aria-label={an.cohorts.map((c) => `${c.year}: ${i18n.money(c.annual)}`).join(', ')}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 160, borderBottom: '1px solid var(--axis)' }}>
                {an.cohorts.map((c) => (
                  <div key={c.year} data-tip={`${c.year} · ${i18n.money(c.annual)} · ${t('insights_v2.policies', { count: c.policies })}`} style={{ flex: 1, height: `${Math.max(c.annual ? 2 : 0, (c.annual / maxCohort) * 100)}%`, background: 'var(--s1)', borderRadius: '4px 4px 0 0', minWidth: 3 }} />
                ))}
              </div>
              <div className="row spread tiny muted" style={{ marginTop: 6 }}><span>{an.cohorts[0]!.year}</span><span>{an.cohorts[an.cohorts.length - 1]!.year}</span></div>
            </div>
          ) : <Empty icon="insights">{t('common.empty')}</Empty>}
        </section>

        <section className="card card-pad" data-testid="insights-activity">
          <div className="row spread"><div className="section-title" style={{ margin: 0 }}>{t('insights_v2.activity')}</div><span className="small muted">{t('insights_v2.weekday', { count: totalDone })}</span></div>
          <div className="heat" style={{ marginTop: 12 }} role="img" aria-label={t('insights_v2.weekday', { count: totalDone })}>
            {Array.from({ length: 7 }, (_, row) => [
              <span key={`l${row}`} style={{ paddingInlineEnd: 6 }}>{wd.format(new Date(`${addDays(start, row)}T12:00:00`))}</span>,
              ...Array.from({ length: 12 }, (_, col) => {
                const a = an.activity[col * 7 + row]!;
                const lvl = a.count ? Math.min(4, Math.ceil((a.count / maxAct) * 4)) : 0;
                return <i key={`${row}-${col}`} style={{ background: `var(--seq-${lvl})` }} data-tip={`${i18n.date(a.day, 'dayMonth')} · ${t('insights_v2.weekday', { count: a.count })}`} />;
              }),
            ])}
          </div>
          <div className="heat-scale" style={{ marginTop: 10, justifyContent: 'flex-end' }}>{t('insights_v2.less')}{[0, 1, 2, 3, 4].map((l) => <i key={l} style={{ background: `var(--seq-${l})` }} />)}{t('insights_v2.more')}</div>
        </section>
      </div>

      <div className="grid grid-2">
        <section className="card card-pad">
          <div className="section-title">{t('insights.mix')}</div>
          {ins.totalAnnual ? (['life', 'general'] as const).map((b) => (
            <div key={b} style={{ marginBottom: 12 }}>
              <div className="row spread small"><span>{i18n.enumLabel('branch', b)}</span><b className="num">{i18n.money(ins.byBranch[b] ?? 0)} · {i18n.percent((ins.byBranch[b] ?? 0) / ins.totalAnnual)}</b></div>
              <div className="bar"><i style={{ width: `${((ins.byBranch[b] ?? 0) / ins.totalAnnual) * 100}%`, background: 'var(--s1)' }} /></div>
            </div>
          )) : <Empty icon="insights">{t('common.empty')}</Empty>}
        </section>
        <section className="card card-pad">
          <div className="section-title">{t('insights.carriers')}</div>
          {ins.topCarriers.length ? ins.topCarriers.map(([name, v]) => (
            <div key={name} style={{ marginBottom: 10 }}>
              <div className="row spread small"><span>{name}</span><b className="num">{i18n.money(v)}</b></div>
              <div className="bar"><i style={{ width: `${(v / maxCarrier) * 100}%`, background: 'var(--s1)' }} /></div>
            </div>
          )) : <Empty icon="insights">{t('common.empty')}</Empty>}
        </section>
      </div>
    </div>
  );
}
